import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize, take } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { NotificationModel, NotificationsService, NotificationTypes } from '../../../shared/services/notifications/notifications.service';
import { SupplierCustomerAcquisition } from '../enums/supplier-customer-acquisition.enum';
import { SupplierCustomerArchived } from '../enums/supplier-customer-archived.enum';
import { SupplierCustomerRepeat } from '../enums/supplier-customer-repeat.enum';
import { SupplierCustomerStatus, SupplierCustomerStatusLabels } from '../enums/supplier-customer-status.enum';
import { SupplierCustomerType } from '../enums/supplier-customer-type.enum';
import { CustomerDto } from '../interfaces/customer-dto.interface';
import { CustomerForm } from '../interfaces/customer-form.interface';
import { CustomerService } from '../services/customer.service';
import { IGeoAddress } from '../../profile/models/geo-address.interface';
import { enumToOptions } from '../../../shared/utils';

interface Option {
  label: string;
  value: string | number;
}

@Component({
    selector: 'customer-form-component',
    templateUrl: './customer-form.control.html',
    styleUrls: ['./customer-form.control.scss'],
    standalone: false
})
export class CustomerFormComponent implements OnInit {
  @Output() formSubmit: EventEmitter<[string, CustomerDto]> = new EventEmitter<[string, CustomerDto]>();
  @Output() formCancel: EventEmitter<void> = new EventEmitter<void>();
  @Input() customerUuid: string;
  customerStatusOptions: Option[] = [];
  acquisitionOptions: Option[] = [];
  isArchivedOptions: Option[] = [];
  customerTypeOptions: Option[] = [];
  customerRepeatOptions: Option[] = [];
  marketingOptions: Option[] = [];
  form: FormGroup<{ [K in keyof CustomerForm]: FormControl<CustomerForm[K]> }>;
  address: IGeoAddress = {};
  addressJson = '{}';
  isLoading = false;
  baseUrl = environment.supplierApiGateway;
  showingTips = false;

  constructor(
    private _customerService: CustomerService,
    private _notificationsService: NotificationsService
  ) {}

  ngOnInit() {
    this.customerStatusOptions = enumToOptions(SupplierCustomerStatus, SupplierCustomerStatusLabels);
    this.acquisitionOptions = Object.entries(SupplierCustomerAcquisition).map(([label, value]) => ({
      label: label === 'Request' ? 'Add To Event Request' : label,
      value
    }));
    this.isArchivedOptions = Object.entries(SupplierCustomerArchived).map(([label, value]) => ({
      label,
      value
    }));
    this.customerTypeOptions = enumToOptions(SupplierCustomerType);
    this.customerRepeatOptions = Object.entries(SupplierCustomerRepeat)
      .filter(([key]) => isNaN(Number(key)))
      .map(([label, value]) => ({
        label: label === 'Oneoff' ? 'One-off' : label,
        value: value
      }));
    this.marketingOptions = [
      { label: 'Yes', value: 'true' },
      { label: 'No', value: 'false' }
    ];
    this.form = new FormGroup<Partial<{ [K in keyof CustomerForm]: FormControl<CustomerForm[K]> }>>({
      status: new FormControl<SupplierCustomerStatus>(SupplierCustomerStatus.ACTIVE_LEAD),
      isArchived: new FormControl<string>('false', { nonNullable: true }),
      acquisition: new FormControl<keyof typeof SupplierCustomerAcquisition>(null),
      honorific: new FormControl<string>('', { nonNullable: true }),
      firstName: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
      lastName: new FormControl<string>('', { nonNullable: true }),
      email: new FormControl<string>(null, { nonNullable: true, validators: [Validators.email] }),
      mobileNumber: new FormControl<string>('', { nonNullable: true }),
      landlineNumber: new FormControl<string>('', { nonNullable: true }),
      companyName: new FormControl<string>('', { nonNullable: true }),
      notes: new FormControl<string>('', { nonNullable: true }),
      type: new FormControl<SupplierCustomerType>(null),
      repeat: new FormControl<SupplierCustomerRepeat>(null),
      marketing: new FormControl<string>('false', { nonNullable: true })
    });

    this.getCustomer(this.customerUuid);
  }

  /**
   * Get a customer by its UUID and patch the form with the returned values.
   *
   * @param customerUuid The UUID of the customer to get.
   */
  getCustomer(customerUuid: string) {
    if (!customerUuid) {
      console.error('No customer UUID provided when trying to get customer');
      return;
    }

    this.isLoading = true;

    this._customerService
      .getCustomer(customerUuid)
      .pipe(
        take(1),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: response => {
          if (!response) {
            return;
          }

          // Patch the form with only the values that the form expects.
          const safeFormValues: CustomerForm = {
            status: response.status ?? null,
            isArchived: response.isArchived ? 'true' : 'false',
            acquisition: response.acquisition ?? null,
            honorific: response.honorific ?? '',
            firstName: response.firstName ?? '',
            lastName: response.lastName ?? '',
            email: response.email ?? '',
            mobileNumber: response.mobileNumber ?? '',
            landlineNumber: response.landlineNumber ?? '',
            companyName: response.companyName ?? '',
            notes: response.notes ?? '',
            type: response.type ?? null,
            repeat: response.repeat ?? null,
            marketing: response.marketing ? 'true' : 'false'
          };
          this.address = response.address ?? {};
          this.addressJson = JSON.stringify(this.address);
          console.log('Patching form with values:', safeFormValues);
          this.form.patchValue(safeFormValues);
        },
        error: error => {
          console.error(error);
          const notification: NotificationModel = {
            message: error.message || `Error getting Customer with ID ${customerUuid}. Please try again.`,
            action: 'Close',
            type: NotificationTypes.ERROR
          };
          this._notificationsService.openSnackBar(notification);
        }
      });
  }

  /**
   * Handler for errorEmitter event of upload elements.
   */
  onFileUploadError(e: { detail: string[] }) {
    console.error(e);
  }

  /**
   * Emits the processed form values for the form. Transform the form data before emitting in this method.
   */
  emitFormSubmit(): void {
    if (!this.form.valid) {
      return;
    }

    // Keep the loading state until the component is destroyed.
    this.isLoading = true;
    const values = {
      ...this.form.value,
      isArchived: this.form.get('isArchived').value === 'true',
      marketing: this.form.get('marketing').value === 'true',
      repeat: this.form.get('repeat').value != null ? Number(this.form.get('repeat').value) : null,
      address: this.address
    } as CustomerDto;

    const customerName = `${this.form.get('firstName').value ?? ''} ${this.form.get('lastName').value ?? ''}`.trim();

    try {
      this.formSubmit.emit([customerName, values]);
    } catch (e) {
      console.error('Error when stringifying product item values', e);
    }
  }

  onAddressChange(event: CustomEvent<{ value: IGeoAddress }>) {
    this.address = event.detail?.value ?? {};
  }

  emitCancelEvent() {
    this.formCancel.emit();
  }
}
